import React, { SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { addApiClient } from 'commons/src/actions/apiClientActions';
import PrimaryButton from 'commons/src/components/buttons/PrimaryButton';
import Input from 'commons/src/components/input/Input';
import InputLabel from 'commons/src/components/input/InputLabel';
import Textarea from 'commons/src/components/input/Textarea';
import { IntercomAPI } from 'commons/src/components/Intercom';
import MapRedirectUris from 'commons/src/features/integrations/apiIntegration/MapRedirectUris';
import { Store } from 'commons/src/reducers';

const MCP_ACCESS_TYPE = 'CONFIDENTIAL';
const MCP_FLOW_TYPE = 'CODE_FLOW';
const MCP_AUTH_SCOPE = 'mcp:read';
const MCP_INTEGRATION_TYPE = 'MCP';

const McpClientForm = (): React.ReactElement => {
    const { t: txt } = useTranslation();
    const dispatch = useDispatch();

    const saveLoading = useSelector((store: Store) => store.commonRequests.ADD_API_CLIENT.loading);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [redirectUris, setRedirectUris] = useState<string[]>([]);
    const [displayValidation, setDisplayValidation] = useState(false);

    const updateName = (e: SyntheticEvent<HTMLInputElement>): void => {
        setName(e.currentTarget.value);
    };

    const hasInvalidUriWildcard = redirectUris.some(uri => uri.includes('*'));
    const hasValidRedirectUris = redirectUris.filter(uri => uri.length > 0).length > 0;

    const validate = (): boolean => {
        const validName = name.length > 0;
        if (validName && hasValidRedirectUris && !hasInvalidUriWildcard) {
            return true;
        }
        setDisplayValidation(true);
        return false;
    };

    const saveClient = (): void => {
        if (!validate()) return;
        const cleanedRedirectUris = redirectUris.filter(uri => uri.length > 0);
        IntercomAPI('trackEvent', 'add-mcp-client', { clientName: name });
        dispatch(
            addApiClient({
                name,
                description,
                active: true,
                accessType: MCP_ACCESS_TYPE,
                flowType: MCP_FLOW_TYPE,
                authScope: [MCP_AUTH_SCOPE],
                integrationType: MCP_INTEGRATION_TYPE,
                redirectUris: cleanedRedirectUris,
            })
        );
    };

    return (
        <div className="page-wrapper__medium page-wrapper__medium--white">
            <div className="settings-details-container">
                <p>{txt('McpIntegration.ClientFormHint')}</p>
                <form>
                    <div className="form__row">
                        <div className="form__field form__field--single-width">
                            <Input
                                type="text"
                                id="mcp-client-name"
                                validate={displayValidation && name.length === 0}
                                isValid={name.length > 0}
                                hint="ApiIntegration.NameHint"
                                label="Name"
                                maxLength={25}
                                currentValue={name}
                                onChange={updateName}
                                testId="mcp-client-name"
                            />
                        </div>
                    </div>
                    <div className="form__row">
                        <div className="form__field">
                            <Textarea
                                autoComplete=""
                                id="mcp-client-description"
                                label="ApiIntegration.Description"
                                maxRows={4}
                                maxLength={255}
                                rows={4}
                                onChange={setDescription}
                                defaultValue={description}
                                displayValidationResult={false}
                                isValid
                                markedMandatory={false}
                                required={false}
                                testId="mcp-client-description"
                            />
                        </div>
                    </div>
                    <div className="form__row">
                        <div className="form__field form__field--single-width">
                            <div className="input-container">
                                <InputLabel htmlFor="mcp-scope" label="ApiIntegration.ResourceScope" />
                                <div className="form__attr input-container__string" id="mcp-scope">
                                    {MCP_AUTH_SCOPE}
                                </div>
                            </div>
                        </div>
                    </div>
                    <MapRedirectUris
                        elementList={redirectUris}
                        updateList={setRedirectUris}
                        displayValidation={displayValidation}
                        infoTextKey="McpIntegration.RedirectUriInfo"
                    />
                    <div className="form__row form__button-container">
                        <div className="form__attr--element centered">
                            <PrimaryButton
                                onClick={saveClient}
                                type="button"
                                title="Save"
                                loading={saveLoading}
                                disabled={hasInvalidUriWildcard}
                                color="primary"
                                testAttr="add-mcp-client"
                                testId="add-mcp-client"
                            />
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default McpClientForm;
